import { useState } from "react";
import styled from "@emotion/styled";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { TickItem } from "recharts/types/util/types";
import { Flex, Text } from "styles/utility-components";

import { RouterTextLink } from "components/actions/RouterLink";
import { currencyFormat } from "components/app_layout/Typography";
import Button from "components/ui/button/Button";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "components/ui/chart/Chart";
import { appRoutes } from "lib/appRoutes";
import SupplierBuyerNoticesModal from "./SupplierBuyerNoticesModal";
import { ChartData, ChartType, OrgDetails } from "./types";
import { ChartConfigType } from "./utils";

const CustomXAxisTick = ({ x, y, payload }: { x: number; y: number; payload: TickItem }) => {
  // currently hardcoded width, ideally this should match the dynamic bar width
  const MAX_LABEL_WIDTH = 90;
  const label = payload.value;

  return (
    <g transform={`translate(${x - MAX_LABEL_WIDTH / 2},${y})`}>
      <foreignObject width={MAX_LABEL_WIDTH} height={20}>
        <XTickLabel width={MAX_LABEL_WIDTH} title={label}>
          {label}
        </XTickLabel>
      </foreignObject>
    </g>
  );
};

function Chart({
  chartType,
  chartData,
  chartConfig,
  selectedBuyer,
  similarNoticeIds,
}: {
  chartType: ChartType;
  chartData: ChartData[];
  chartConfig: ChartConfigType;
  selectedBuyer: OrgDetails;
  similarNoticeIds: string[];
}) {
  const [selectedSupplier, setSelectedSupplier] = useState<ChartData | undefined>(undefined);

  const onViewNotices = (data: ChartData) => setSelectedSupplier(data);

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedSupplier(undefined);
    }
  };

  const gradientKey = `${chartType}-gradient`;

  const minChartContainerWidth = chartData.length * 120;

  return (
    <>
      <ScrollWrapper>
        <StyledChartContainer config={chartConfig} minWidth={minChartContainerWidth}>
          <BarChart accessibilityLayer data={chartData}>
            <ChartLegend content={<LegendContent />} verticalAlign="top" align="left" />
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="supplier.name"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              interval={0}
              tick={(props) => <CustomXAxisTick {...props} />}
            />
            <YAxis tickLine={false} tickMargin={10} axisLine={false} allowDecimals={false} />
            <ChartTooltip
              cursor={false}
              position={{ y: 100 }}
              content={<ChartTooltipContent hideLabel />}
              formatter={(_value, name, entry) => {
                const item = entry.payload;
                // doing this so it only renders the data once
                if (name === "Total awards") {
                  return (
                    <>
                      <TooltipWrapper column gap={12}>
                        <LinkWrapper>
                          <RouterTextLink {...appRoutes.supplierDetail(item.supplier.guid)}>
                            {item.supplier.name}
                          </RouterTextLink>
                        </LinkWrapper>
                        <Flex gap={19} column>
                          <Flex alignItems="baseline" gap={8}>
                            <ChartKey color={chartConfig.nonSimilarAwards.color} />
                            <Flex column alignItems="flex-start">
                              <Text>{item.totalAwards} total awards</Text>
                              <Text description color="sysTextSecondary">
                                {currencyFormat(item.allAwardsTotalValue, "GBP")} total value
                              </Text>
                            </Flex>
                          </Flex>
                          <Flex alignItems="baseline" gap={8}>
                            <ChartKey color={chartConfig.similarAwards.color} />
                            <Flex column alignItems="flex-start">
                              <Text>{item.similarAwards} similar awards</Text>
                              <Text description color="sysTextSecondary">
                                {currencyFormat(item.similarAwardsTotalValue, "GBP")} total value
                              </Text>
                            </Flex>
                          </Flex>
                          <ViewButton buttonType="secondary" onClick={() => onViewNotices(item)}>
                            View
                          </ViewButton>
                        </Flex>
                      </TooltipWrapper>
                    </>
                  );
                }
                return null;
              }}
            />
            <defs>
              <linearGradient id={gradientKey} x1="0" y1="0" x2="0" y2="1">
                <stop offset="20%" stopColor={chartConfig.nonSimilarAwards.gradientStartColor} />
                <stop offset="100%" stopColor={chartConfig.nonSimilarAwards.gradientEndColor} />
              </linearGradient>
            </defs>
            <Bar
              cursor="pointer"
              dataKey="similarAwards"
              stackId="awards"
              fill="var(--color-similarAwards)"
              name="Awards similar to this opportunity"
              onClick={(item) => onViewNotices(item.payload)}
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => {
                return entry.similarAwards === entry.totalAwards ? (
                  <Cell key={`cell-${index}`} />
                ) : (
                  <Cell key={`cell-${index}`} radius={0} />
                );
              })}
            </Bar>
            <Bar
              cursor="pointer"
              stackId="awards"
              dataKey="nonSimilarAwards"
              fill={`url(#${gradientKey})`}
              radius={[4, 4, 0, 0]}
              name="Total awards"
              onClick={(item) => onViewNotices(item.payload)}
            />
          </BarChart>
        </StyledChartContainer>
      </ScrollWrapper>
      <SupplierBuyerNoticesModal
        isOpen={!!selectedSupplier}
        onOpenChange={handleOnOpenChange}
        supplier={{
          guid: selectedSupplier?.supplier.guid ?? "",
          name: selectedSupplier?.supplier.name ?? "",
          logoUrl: selectedSupplier?.supplier.logoUrl,
        }}
        buyer={{
          guid: selectedBuyer.guid,
          name: selectedBuyer.name,
          logoUrl: selectedBuyer.logoUrl,
        }}
        similarNoticesIds={similarNoticeIds}
      />
    </>
  );
}

const ScrollWrapper = styled(Flex)({
  flexDirection: "column",
  width: "100%",
  overflowX: "auto",
});

const StyledChartContainer = styled(ChartContainer)<{ minWidth: number }>(({ minWidth }) => ({
  minWidth: `${minWidth}px`,
  height: 400,
}));

const ChartKey = styled.div<{ color?: string }>((props) => ({
  backgroundColor: props.color,
  width: 13,
  height: 13,
  borderRadius: 4,
}));

const LegendContent = styled(ChartLegendContent)({
  paddingTop: 16,
  paddingBottom: 24,
  paddingLeft: 28,
});

const ViewButton = styled(Button)({
  // Ensures button remains interactive (hover/focus styles are applied)
  pointerEvents: "all",
});

const TooltipWrapper = styled(Flex)({
  padding: 5,
});

const LinkWrapper = styled(Flex)({
  // forces the link inside the tooltip to be clickable
  pointerEvents: "all",
});

const XTickLabel = styled.div<{ width: number }>(({ width }) => ({
  width: `${width}px`,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  textAlign: "center",
}));

export default Chart;
